import CoachBotPrompts from "app/containers/CoachBotPrompts";
import TeamsTab from "app/containers/TeamsTab";
import Login from "app/containers/Login";
import McpAuthorize from "app/containers/McpAuthorize";
import Loading from "app/storybookComponents/Loading";
import OrganizationGuide from "app/containers/CompanyGuide";
import { Navigate, Route, Routes } from "react-router-dom";
import Dashboard from "app/containers/Dashboard";
import Search from "app/containers/Search";
import UserSettings from "app/containers/UserSettings";
import TeamGuide from "app/containers/TeamGuide";
import UserGuide from "app/containers/UserGuide";
import DepartmentGuide from "app/containers/DepartmentGuide";
import SkillsGuide from "app/containers/SkillsGuide";
import HiddenAdminConsole from "app/containers/HiddenAdminConsole";
import AdminConsole from "app/containers/AdminConsole";
import AdvancedAnalytics from "app/containers/AdvancedAnalytics";
import { useAppSelector } from "utils/redux/hooks";
import {
  selectCompanyInfo,
  selectCurrentUserAccountId,
  selectGetUserStatus,
  selectIsCurrentUserAdmin,
} from "app/containers/Global/slice";
import {
  selectGetTeamAndDepartmentLeadsStatus,
  selectIsDepartmentsHidden,
  selectTeamAndDepartmentLeadIdsForLoggedInUser,
} from "app/containers/AdminConsole/slice";
import AccountSetup from "app/containers/AccountSetup";
import ResetPasswordFlow from "app/containers/Login/ResetPasswordFlow";
import Assessment from "app/containers/Assessment";
import LoginlessWeeklyCheckinView from "app/containers/LoginlessWeeklyCheckinView";
import AdvancedCsvUpload from "app/containers/AdvancedCsvUpload";
import SurveyBreakdown from "app/containers/SurveyBreakdown";
import SubordinatesWeeklyCheckinTable from "app/containers/WeeklyCheckInMonitor/SubordinatesWeeklyCheckinTableIndex";
import Subscribe from "app/containers/Subscribe";

// Redirects "/" before the Dashboard mounts, so we don't fire the home page requests just to navigate away from them.
function RootRedirect() {
  const isAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const getUserStatus = useAppSelector(selectGetUserStatus);

  if (getUserStatus === "succeeded") {
    return (
      <Navigate to={isAdmin ? "/AdminConsole/Dashboard" : "/home"} replace />
    );
  }
  if (getUserStatus === "failed") {
    return <Dashboard />;
  }
  return <Loading />;
}

export default function AllRoutes() {
  const user = useAppSelector(selectCurrentUserAccountId);
  const isAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const getTeamAndDepartmentLeadsStatus = useAppSelector(
    selectGetTeamAndDepartmentLeadsStatus
  );
  const companyInfo = useAppSelector(selectCompanyInfo);
  const isDepartmentsHidden = useAppSelector(selectIsDepartmentsHidden);
  const teamAndDepartmentLeaderOf = useAppSelector(
    selectTeamAndDepartmentLeadIdsForLoggedInUser
  );

  const getDepartmentPaths = () => {
    if (isDepartmentsHidden) {
      return null;
    }

    return (
      <>
        <Route path="/Search/Departments" element={<Search />} />
        <Route
          path="/DepartmentGuide/:departmentId"
          element={<DepartmentGuide />}
        />
      </>
    );
  };

  const getAdminPaths = () => {
    if (!isAdmin) {
      return null;
    }

    return (
      <>
        <Route path="/HiddenAdminConsole" element={<HiddenAdminConsole />} />
        <Route path="/AdminConsole" element={<AdminConsole />} />
        <Route path="/AdminConsole/:tabSelected" element={<AdminConsole />} />
        <Route
          path="/AdminConsole/:tabSelected/:subTabSelected"
          element={<AdminConsole />}
        />
        <Route path="/AdvancedAnalytics" element={<AdvancedAnalytics />} />
      </>
    );
  };

  const getTeamLeaderPaths = () => {
    // If not admin and if we have not fetched the team and department leads then we need to show the loading screen for the team leader paths.
    if (!isAdmin && getTeamAndDepartmentLeadsStatus === "loading") {
      const teamLeaderPaths = ["/TeamGuide/:teamId/TeamSettings"];
      return teamLeaderPaths.map((path) => (
        <Route key={path} path={path} element={<Loading />} />
      ));
    }

    // If not admin and if the user is not a team leader then we need to return null. so that the team leader paths are not shown.
    if (!isAdmin && !teamAndDepartmentLeaderOf?.teams?.length) {
      return null;
    }

    // If the user is a team leader then we need to show the team leader paths.
    return (
      <Route path="/TeamGuide/:teamId/TeamSettings" element={<TeamGuide />} />
    );
  };

  const getUserPaths = () => {
    // If the user is not logged in then we need to show the login page.
    if (!user) {
      return <Route path="*" element={<Login />} />;
    }

    return (
      <>
        {getAdminPaths()}
        {getDepartmentPaths()}
        {getTeamLeaderPaths()}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/subscribe" element={<Subscribe />} />
        <Route path="/home" element={<Dashboard />} />
        <Route path="/Search/People" element={<Search />} />
        <Route path="/Search/Teams" element={<Search />} />
        <Route path="/Search/DirectReports" element={<Search />} />
        <Route path="/UserSettings" element={<UserSettings />} />
        <Route path="/UserSettings/:tab" element={<UserSettings />} />
        <Route path="/OrganizationGuide" element={<OrganizationGuide />} />
        <Route path="/TeamGuide/:teamId" element={<TeamGuide />} />
        <Route path="/TeamGuide/:teamId/TeamMembers" element={<TeamGuide />} />
        <Route
          path="/TeamGuide/:teamId/TeamPersonality"
          element={<TeamGuide />}
        />
        <Route path="/UserGuide" element={<UserGuide />} />
        <Route path="/SurveyBreakdown" element={<SurveyBreakdown />} />
        <Route path="/UserGuide/:userAccountId" element={<UserGuide />} />
        <Route path="/SkillsGuide" element={<SkillsGuide />} />
        {!companyInfo?.freeTrial && (
          <Route path="/AdvancedCsvUpload" element={<AdvancedCsvUpload />} />
        )}
        <Route path="/CoachBoPrompts" element={<CoachBotPrompts />} />
        <Route
          path="/DirectReportsWeeklyCheckin"
          element={<SubordinatesWeeklyCheckinTable />}
        />
        {/* // PLOP_ROUTE */}
        {/* Using path="*"" means "match anything", so this route
                acts like a catch-all for URLs that we don't have explicit
              routes for. */}
        <Route path="*" element={<div>404: Page Not Found</div>} />
      </>
    );
  };

  return (
    <Routes>
      <Route path="/teams/tab" element={<TeamsTab />} />
      <Route path="/accountSetup" element={<AccountSetup />} />
      <Route path="/PasswordReset" element={<ResetPasswordFlow />} />
      <Route path="join/:joinType/:inviteCode" element={<AccountSetup />} />
      <Route path="/survey/team360" element={<Assessment />} />
      <Route
        path="/weekly-checkin/:encryptedThreadId"
        element={<LoginlessWeeklyCheckinView />}
      />
      <Route path="/mcp-authorize" element={<McpAuthorize />} />
      {getUserPaths()}
    </Routes>
  );
}
